<script lang="ts" generic="T, U extends boolean = false" setup>
import * as _ from 'lodash-es'

import { ObjectUtils } from 'primevue/utils'
import AutoComplete from 'primevue/autocomplete'
import type {
  AutoCompleteCompleteEvent,
  AutoCompleteDropdownClickEvent,
  AutoCompleteChangeEvent,
  AutoCompleteItemSelectEvent,
  AutoCompleteItemUnselectEvent,
} from 'primevue/autocomplete'
import type { HTMLAttributes, InputHTMLAttributes } from 'vue'
import type { VirtualScrollerProps, VirtualScrollerItemOptions } from 'primevue/virtualscroller'
import type { AutoCompletePassThroughOptions } from 'primevue/autocomplete'
type PassThroughMergePropsType = ((...args: any) => object | undefined) | boolean | undefined
interface PassThroughOptions {
  mergeSections?: boolean | undefined
  mergeProps?: PassThroughMergePropsType
}
type PassThrough<T = void> = T | object | undefined
const props = withDefaults(
  defineProps<{
    /**
     * Value of the component.
     */
    modelValue?: U extends true ? T[] : T
    /**
     * An array of suggestions to display.
     */
    getSuggestions?: (query: string, options: { abortSignal: AbortSignal }) => T[] | Promise<T[]>
    /**
     * @deprecated since v3.16.0. Use 'optionLabel' property instead.
     * Property name or getter function of a suggested object to resolve and display.
     */
    field?: string | ((data: any) => string) | undefined
    /**
     * Property name or getter function to use as the label of an option.
     */
    optionLabel?: string | ((data: any) => string) | undefined
    /**
     * Property name or getter function to use as the disabled flag of an option, defaults to false when not defined.
     */
    optionDisabled?: string | ((data: any) => boolean) | undefined
    /**
     * Property name or getter function to use as the label of an option group.
     */
    optionGroupLabel?: string | ((data: any) => string) | undefined
    /**
     * Property name or getter function that refers to the children options of option group.
     */
    optionGroupChildren?: string | ((data: any) => any[]) | undefined
    /**
     * Maximum height of the suggestions panel.
     * @defaultValue 200px
     */
    scrollHeight?: string | undefined
    /**
     * Displays a button next to the input field when enabled.
     * @defaultValue false
     */
    dropdown?: boolean
    /**
     * Specifies the behavior dropdown button. Default 'blank' mode sends an empty string and 'current' mode sends the input value.
     * @defaultValue blank
     */
    dropdownMode?: 'blank' | 'current' | undefined
    /**
     * @deprecated since v3.16.0
     * Highlights automatically the first item of the dropdown to be selected.
     * @defaultValue false
     */
    autoHighlight?: boolean
    /**
     * Specifies if multiple values can be selected.
     * @defaultValue false
     */
    multiple?: U
    /**
     * Default text to display when no option is selected.
     */
    placeholder?: string | undefined
    /**
     * Whether the autocomplete is in forced loading state.
     * @defaultValue false
     */
    loading?: boolean | undefined
    /**
     * When present, it specifies that the component should have invalid state style.
     * @defaultValue false
     */
    invalid?: boolean
    /**
     * When present, it specifies that the component should be disabled.
     * @defaultValue false
     */
    disabled?: boolean
    /**
     * Specifies the input variant of the component.
     * @defaultValue outlined
     */
    variant?: 'outlined' | 'filled' | undefined
    /**
     * A property to uniquely identify an option.
     */
    dataKey?: string | undefined
    /**
     * Minimum number of characters to initiate a search.
     * @defaultValue 1
     */
    minLength?: number | undefined
    /**
     * Delay between keystrokes to wait before sending a query.
     * @defaultValue 300
     */
    delay?: number | undefined
    /**
     * A valid query selector or an HTMLElement to specify where the overlay gets attached.
     * Special keywords are 'body' for document body and 'self' for the element itself.
     * @defaultValue body
     */
    appendTo?: 'body' | 'self' | string | undefined | HTMLElement
    /**
     * When present, autocomplete clears the manual input if it does not match of the suggestions to force only accepting values from the suggestions.
     * @defaultValue false
     */
    forceSelection?: boolean
    /**
     * Whether to run a query when input receives focus.
     * @defaultValue false
     */
    completeOnFocus?: boolean
    /**
     * Identifier of the underlying input element.
     */
    inputId?: string | undefined
    /**
     * Inline style of the input field.
     */
    inputStyle?: object | undefined
    /**
     * Style class of the input field.
     */
    inputClass?: string | object | undefined
    /**
     * Used to pass all properties of the HTMLInputElement to the focusable input element inside the component.
     */
    inputProps?: InputHTMLAttributes | undefined
    /**
     * Inline style of the overlay panel.
     */
    panelStyle?: object | undefined
    /**
     * Style class of the overlay panel.
     */
    panelClass?: string | object | undefined
    /**
     * Used to pass all properties of the HTMLDivElement to the overlay panel inside the component.
     */
    panelProps?: HTMLAttributes | undefined
    /**
     * Icon to display in the dropdown.
     * @deprecated since v3.27.0. Use 'dropdownicon' slot.
     */
    dropdownIcon?: string | undefined
    /**
     * Style class of the dropdown button.
     */
    dropdownClass?: string | object | undefined
    /**
     * Icon to display in loading state.
     * @deprecated since v3.27.0. Use 'loadingicon' slot.
     */
    loadingIcon?: string | undefined
    /**
     * Icon to display in chip remove action.
     * @deprecated since v3.27.0. Use 'removetokenicon' slot.
     */
    removeTokenIcon?: string | undefined
    /**
     * Whether to use the virtualScroller feature. The properties of VirtualScroller component can be used like an object in it.
     * @type {VirtualScrollerProps}
     */
    virtualScrollerOptions?: VirtualScrollerProps
    /**
     * Whether to focus on the first visible or selected element when the overlay panel is shown.
     * @defaultValue false
     */
    autoOptionFocus?: boolean
    /**
     * When enabled, the focused option is selected.
     * @defaultValue false
     */
    selectOnFocus?: boolean
    /**
     * When enabled, the focus is placed on the hovered option.
     * @defaultValue true
     */
    focusOnHover?: boolean
    /**
     * Locale to use in searching. The default locale is the host environment's current locale.
     */
    searchLocale?: string | undefined
    /**
     * Text to be displayed in hidden accessible field when filtering returns any results. Defaults to value from PrimeVue locale configuration.
     * @defaultValue '{0} results are available'
     */
    searchMessage?: string | undefined
    /**
     * Text to be displayed in hidden accessible field when options are selected. Defaults to value from PrimeVue locale configuration.
     * @defaultValue '{0} items selected'
     */
    selectionMessage?: string | undefined
    /**
     * Text to be displayed in hidden accessible field when any option is not selected. Defaults to value from PrimeVue locale configuration.
     * @defaultValue No selected item
     */
    emptySelectionMessage?: string | undefined
    /**
     * Text to display when filtering does not return any results. Defaults to value from PrimeVue locale configuration.
     * @defaultValue No results found
     */
    emptySearchMessage?: string | undefined
    /**
     * Index of the element in tabbing order.
     */
    tabindex?: number | string | undefined
    /**
     * Defines a string value that labels an interactive element.
     */
    ariaLabel?: string | undefined
    /**
     * Identifier of the underlying input element.
     */
    ariaLabelledby?: string | undefined
    /**
     * Used to pass attributes to DOM elements inside the component.
     * @type {AutoCompletePassThroughOptions}
     */
    pt?: PassThrough<AutoCompletePassThroughOptions>
    /**
     * Used to configure passthrough(pt) options of the component.
     * @type {PassThroughOptions}
     */
    ptOptions?: PassThroughOptions
    /**
     * When enabled, it removes component related styles in the core.
     * @defaultValue false
     */
    unstyled?: boolean
  }>(),
  {
    getSuggestions: () => () => [],
    dropdown: false,
    autoHighlight: false,
    invalid: false,
    disabled: false,
    forceSelection: false,
    completeOnFocus: false,
    unstyled: false,
    autoOptionFocus: false,
    selectOnFocus: false,
    focusOnHover: true,
  },
)
const emit = defineEmits<{
  /**
   * Emitted when the value changes.
   * @param {*} value - New value.
   */
  (e: 'update:modelValue', value: any): void
  /**
   * Callback to invoke on value change.
   * @param {AutoCompleteChangeEvent} event - Custom change event.
   */
  (e: 'change', event: AutoCompleteChangeEvent): void
  /**
   * Callback to invoke when the component receives focus.
   * @param {Event} event - Browser event.
   */
  (e: 'focus', event: Event): void
  /**
   * Callback to invoke when the component loses focus.
   * @param {Event} event - Browser event.
   */
  (e: 'blur', event: Event): void
  /**
   * Callback to invoke when a suggestion is selected.
   * @param {AutoCompleteItemSelectEvent} event - Custom item select event.
   */
  (e: 'item-select', event: AutoCompleteItemSelectEvent): void
  /**
   * Callback to invoke when a selected value is removed.
   * @param {AutoCompleteItemUnselectEvent} event - Custom item unselect event.
   */
  (e: 'item-unselect', event: AutoCompleteItemUnselectEvent): void
  /**
   * Callback to invoke to when dropdown button is clicked.
   * @param {AutoCompleteDropdownClickEvent} event - Custom dropdown click event.
   */
  (e: 'dropdown-click', event: AutoCompleteDropdownClickEvent): void
  /**
   * Callback to invoke when input is cleared by the user.
   */
  (e: 'clear'): void
  /**
   * Callback to invoke to search for suggestions.
   * @param {AutoCompleteCompleteEvent} event - Custom complete event.
   */
  (e: 'complete', event: AutoCompleteCompleteEvent): void
  /**
   * Callback to invoke before the overlay is shown.
   */
  (e: 'before-show'): void
  /**
   * Callback to invoke before the overlay is hidden.
   */
  (e: 'before-hide'): void
  /**
   * Callback to invoke when the overlay is shown.
   */
  (e: 'show'): void
  /**
   * Callback to invoke when the overlay is hidden.
   */
  (e: 'hide'): void
}>()
defineSlots<{
  /**
   * Custom chip template.
   * @param {Object} scope - chip slot's params.
   */
  chip(scope: {
    /**
     * A value in the selection
     */
    value: any
  }): VNode[]
  /**
   * Custom header template of panel.
   * @param {Object} scope - header slot's params.
   */
  header(scope: {
    /**
     * Value of the component
     */
    value: any
    /**
     * Displayed options
     */
    suggestions: any[]
  }): VNode[]
  /**
   * Custom footer template of panel.
   * @param {Object} scope - footer slot's params.
   */
  footer(scope: {
    /**
     * Value of the component
     */
    value: any
    /**
     * Displayed options
     */
    suggestions: any[]
  }): VNode[]
  /**
   * @deprecated since v3.16.0
   * Custom content for each item.
   * @param {Object} scope - item slot's params.
   */
  item(scope: {
    /**
     * Option instance
     */
    item: any
    /**
     * Index of the option
     */
    index: number
  }): VNode[]
  /**
   * Custom option template.
   * @param {Object} scope - option slot's params.
   */
  option(scope: {
    /**
     * Option instance
     */
    option: any
    /**
     * Index of the option
     */
    index: number
  }): VNode[]
  /**
   * Custom option group template.
   * @param {Object} scope - option group slot's params.
   */
  optiongroup(scope: {
    /**
     * @deprecated since v3.16.0. Use the 'option' instead.
     * Option instance
     */
    item: any
    /**
     * Option instance
     */
    option: any
    /**
     * Index of the option
     */
    index: number
  }): VNode[]
  /**
   * Custom content template.
   * @param {Object} scope - content slot's params.
   */
  content(scope: {
    /**
     * An array of objects to display for virtualscroller
     */
    items: any
    /**
     * Style class of the component
     */
    styleClass: string
    /**
     * Referance of the content
     * @param {HTMLElement} el - Element of 'ref' property
     */
    contentRef: (el: any) => void
    /**
     * Options of the items
     * @param {number} index - Rendered index
     * @return {VirtualScrollerItemOptions}
     */
    getItemOptions: (index: number) => VirtualScrollerItemOptions
  }): VNode[]
  /**
   * Custom loader template.
   * @param {Object} scope - loader slot's params.
   */
  loader(scope: {
    /**
     * Options of the loader items for virtualscroller
     */
    options: any[]
  }): VNode[]
  /**
   * Custom empty template when there is no data to display.
   */
  empty(): VNode[]
  /**
   * Custom dropdown icon template.
   */
  dropdownicon(scope: {
    /**
     * Style class of the icon.
     */
    class: string
  }): VNode[]
  /**
   * Custom remove token icon template in multiple mode.
   */
  removetokenicon(scope: {
    /**
     * Style class of the icon.
     */
    class: string
    /**
     * Index of the token.
     */
    index: number
    /**
     * Remove token icon function.
     * @param {Event} event - Browser event
     * @deprecated since v3.39.0. Use 'removeCallback' property instead.
     */
    onClick: (event: Event, index: number) => void
    /**
     * Remove token icon function.
     * @param {Event} event - Browser event
     */
    removeCallback: (event: Event, index: number) => void
  }): VNode[]
  /**
   * Custom loading icon template.
   */
  loadingicon(scope: {
    /**
     * Style class of the loading icon.
     */
    class: string
  }): VNode[]
}>()

const booleanMultiple = computed(() =>
  typeof props.multiple === 'boolean' ? props.multiple : props.multiple != null,
)
const clearedModelValue = computed(() =>
  booleanMultiple.value && (props.modelValue as any[])?.length === 0 ? null : props.modelValue,
)
const onUpdateModelValue = (value: (typeof props)['modelValue']) =>
  emit('update:modelValue', booleanMultiple.value && value == null ? [] : value)
const passedProps = reactiveOmit(props, 'modelValue', 'multiple', 'loading', 'getSuggestions')
const passedAttrs = _.omit(useAttrs(), 'onComplete', 'onUpdate:modelValue')
const internalLoading = ref(false)
const suggestions = ref<T[]>([])
const actualLoading = computed(() =>
  typeof props.loading === 'boolean' ? props.loading : internalLoading.value,
)
let requestCounter = 0
let abortController: AbortController | undefined
const searchQuery = ref('')
function search(event: { query: string }) {
  abortController?.abort()
  abortController = new AbortController()
  internalLoading.value = true
  const requestCounterAtStart = ++requestCounter
  searchQuery.value = event.query

  Promise.resolve(
    props.getSuggestions(event.query, {
      abortSignal: abortController.signal,
    }),
  )
    .catch(error => {
      // Keep previous suggestions when request is aborted
      if (error?.name === 'AbortError') return suggestions.value
      // Otherwise in an error state, clear suggestions
      return []
    })
    .then(data => {
      if (requestCounterAtStart !== requestCounter) return
      internalLoading.value = false

      // Prevent duplicates
      if (booleanMultiple.value) {
        data = (data as any[]).filter(
          item => !(props.modelValue as any[]).some(value => _.isEqual(value, item)),
        )
      }

      ;(suggestions as any).value = data
    })
}

function getOptionHtml(option: T) {
  const label: string = props.optionLabel
    ? ObjectUtils.resolveFieldData(option, props.optionLabel as any)
    : option
  const escapedLabel = _.escape(label)
  if (searchQuery.value.length === 0) return escapedLabel

  return label.replaceAll(
    new RegExp(_.escapeRegExp(searchQuery.value), 'ig'),
    match => `<mark class="remote-autocomplete-mark">${match}</mark>`,
  )
}

// Properly focus input when clicking on the container
function onMousedown(event: MouseEvent) {
  if (!(event.target instanceof HTMLElement)) return

  if (
    event.target.classList.contains('p-autocomplete-multiple-container') ||
    event.target.classList.contains('p-autocomplete-input-token')
  ) {
    event.preventDefault()
    ;(event.target.querySelector('[role="combobox"]') as HTMLInputElement).focus()
  }
}

const focused = ref(false)
const input = ref<HTMLInputElement>()
function onFocus(event: Event) {
  input.value = event.target as HTMLInputElement
  focused.value = true
}
function onBlur(event: Event) {
  input.value = event.target as HTMLInputElement
  focused.value = false
}

const overlayVisible = ref(false)
const onShow = () => (overlayVisible.value = true)
const onHide = () => (overlayVisible.value = false)

// Clear input field on blur
function clearInput() {
  if (!input.value) return
  if (input.value.value.length === 0) return

  input.value.value = ''
  input.value.dispatchEvent(new InputEvent('input', { bubbles: true }))
}
watch(() => focused.value === false && overlayVisible.value === false, clearInput)
</script>

<template>
  <AutoComplete
    class="remote-autocomplete"
    :loading="actualLoading"
    :suggestions="suggestions"
    v-bind="{ ...(passedProps as any), ...passedAttrs, multiple: booleanMultiple }"
    @complete="search"
    :model-value="clearedModelValue"
    @update:model-value="onUpdateModelValue"
    @mousedown="onMousedown"
    @before-show="onShow"
    @before-hide="onHide"
    @focus="onFocus"
    @blur="onBlur"
  >
    <template #option="{ option, index }"><span v-html="getOptionHtml(option)" /></template>
    <!-- @vue-skip Skip lackluster TypeScript validation -->
    <template v-for="(_, name) in $slots" v-slot:[name]="slotData"
      ><slot :name="name" v-bind="slotData"
    /></template>
  </AutoComplete>
</template>

<style lang="scss" scoped>
.remote-autocomplete {
  display: flex;
  width: 100%;

  :deep(> .p-autocomplete-multiple-container) {
    width: 100%;
  }

  :deep(.p-autocomplete-input-token input) {
    line-height: 1.5;
  }
}

:global(.p-autocomplete-empty-message) {
  padding: 0.2em 0.25em;
}
</style>
